import { axios } from '../index';
import config from '../config';

// Contract: vyce-api-gateway, modules/reports/api/teams_reports (features/VYCE-2907-teams-reports).
// One router for the work and Legend apps alike — a Legend is answered company-wide.

export type TeamsReportType = 'company_snapshot' | 'individual';
export type TeamsReportClientApp = 'legend' | 'work';
export type TeamsReportNotifyChannel = 'email' | 'messaging';
export type TeamsReportStat =
  | 'headcount'
  | 'workforce_by_role'
  | 'nationalities'
  | 'gender_split'
  | 'rtw_status'
  | 'id_verification'
  | 'qualifications_coverage'
  | 'team_proximity';
export type TeamsReportSection = 'professional' | 'qualifications' | 'id_rtw' | 'personal';
export type TeamsReportFieldSet = 'lean' | 'full';
export type TeamsReportAnalysisMode = 'none' | 'preset' | 'custom';
export type TeamsReportAnalysisPreset = 'building_safety_regulations';
export type TeamsReportStatus = 'pending' | 'running' | 'completed' | 'failed' | 'expired';

export interface TeamsReportCreatePayload {
  report_type: TeamsReportType;
  client_app: TeamsReportClientApp;
  /** team_ids for a snapshot (null = whole company), user_ids for an individual report. */
  scope: { team_ids?: string[] | null; user_ids?: string[] };
  snapshot?: { stats: TeamsReportStat[] };
  individual?: {
    sections: TeamsReportSection[];
    /** null = every qualification the people hold. */
    qualification_titles: string[] | null;
    field_set: TeamsReportFieldSet;
  };
  analysis: {
    mode: TeamsReportAnalysisMode;
    preset?: TeamsReportAnalysisPreset;
    request_text?: string;
  };
  notify: { channels: TeamsReportNotifyChannel[]; user_ids: string[] };
}

export interface TeamsReportAccepted {
  request_id: string;
  status: 'pending';
}

export interface TeamsReportRequest {
  request_id: string;
  report_type: TeamsReportType;
  client_app: TeamsReportClientApp;
  status: TeamsReportStatus;
  requested_by: { user_id: string; name: string; actor_type: 'user' | 'legend' };
  scope_summary: {
    /** null = the whole company. */
    team_ids: string[] | null;
    team_names: string[];
    user_ids: string[];
    /** null for snapshots. */
    people_count: number | null;
    locations: { id: string; name: string }[];
  };
  sections: TeamsReportSection[];
  stats: TeamsReportStat[];
  field_set: TeamsReportFieldSet | null;
  analysis_mode: TeamsReportAnalysisMode;
  notify: { channels: TeamsReportNotifyChannel[]; user_ids: string[] };
  /** Naive UTC, no offset in the string. */
  notified_at: string | null;
  contains_pii: boolean;
  file_name: string | null;
  page_count: number | null;
  size_bytes: number | null;
  /** Set when failed, or "analysis_failed" on a completed report that has no analysis. */
  error_code: string | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string;
}

export interface TeamsReportList {
  items: TeamsReportRequest[];
  total: number;
}

export interface TeamsReportDownload {
  /** Presigned, valid for 15 minutes: open it straight away, never keep it. */
  url: string;
  file_name: string;
  expires_at: string;
  size_bytes: number | null;
}

const reportsUrl = (companyId: string) =>
  `${config.gatewayApiUrl}/public/v1/reports/companies/${companyId}/teams-reports`;

export const getReportQualificationTitlesRequest = (
  companyId: string,
  params: { user_ids?: string[]; team_ids?: string[] }
) =>
  // The shared serializer repeats the key (user_ids=a&user_ids=b), which is how FastAPI reads a list
  axios.get<string[]>(`${reportsUrl(companyId)}/qualification-titles`, { params });

export const createTeamsReportRequest = (companyId: string, payload: TeamsReportCreatePayload) =>
  axios.post<TeamsReportAccepted>(reportsUrl(companyId), payload);

export const getTeamsReportsRequest = (companyId: string, params: { page: number; page_size: number }) =>
  axios.get<TeamsReportList>(reportsUrl(companyId), { params });

export const getTeamsReportRequest = (companyId: string, requestId: string) =>
  axios.get<TeamsReportRequest>(`${reportsUrl(companyId)}/${requestId}`);

export const downloadTeamsReportRequest = (companyId: string, requestId: string) =>
  axios.get<TeamsReportDownload>(`${reportsUrl(companyId)}/${requestId}/download`);

export const deleteTeamsReportRequest = (companyId: string, requestId: string) =>
  axios.delete(`${reportsUrl(companyId)}/${requestId}`);
